import React from 'react';
import { 
  IndianRupee, 
  BarChart3, 
  AlertTriangle,
  Target,
  PieChart,
  Activity,
  Shield,
  CreditCard,
  Zap
} from 'lucide-react';
import { 
  formatCurrency,
  IncomeConsistency,
  CreditWorthinessSummary,
  InvestmentTransaction,
  LoanPaymentRatio,
  ExpenseRatio,
  InvestmentRatio,
  ExceptionalTransaction,
  MonthlyOverdraft,
  HighValueTransaction,
  IrregularIncome,
  SuddenLargeWithdrawal
} from '../utils/bankStatementUtils';

// Reusable Card Component
const AnalysisCard: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ 
  title, 
  children, 
  className = "" 
}) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${className}`}>
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Income Consistency Analysis Component
export const IncomeConsistencyAnalysis: React.FC<{ data: IncomeConsistency[] }> = ({ data }) => {
  // Handle cases where data might be an empty object or invalid
  if (!data || !Array.isArray(data) || !data.length) return null;

  const stableMonths = data.filter(item => item.ConsistencyRating === 'Stable').length;
  const unstableMonths = data.filter(item => item.ConsistencyRating === 'Unstable').length;
  const consistencyPercentage = (stableMonths / data.length) * 100;

  return (
    <AnalysisCard title="Income Consistency Analysis">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Consistency Score */}
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                strokeDasharray={`${consistencyPercentage * 2.51} 251`}
                className={consistencyPercentage >= 80 ? 'text-green-500' : consistencyPercentage >= 60 ? 'text-yellow-500' : 'text-red-500'}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-gray-900">{Math.round(consistencyPercentage)}%</span>
            </div>
          </div>
          <p className="text-sm text-gray-600">Income Consistency</p>
        </div>

        {/* Statistics */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Stable Months:</span>
            <span className="font-semibold text-green-600">{stableMonths}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Unstable Months:</span>
            <span className="font-semibold text-red-600">{unstableMonths}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Months:</span>
            <span className="font-semibold">{data.length}</span>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="mt-6">
        <h4 className="text-md font-semibold mb-3">Monthly Income Trends</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Income</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trend</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.slice(0, 6).map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.Month}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(item.MonthlyIncome)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item.ConsistencyRating === 'Stable' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.ConsistencyRating}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item.IncomeTrendRating === 'Positive Growth' 
                        ? 'bg-green-100 text-green-800' 
                        : item.IncomeTrendRating === 'Declining'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.IncomeTrendRating}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AnalysisCard>
  );
};

// Credit Worthiness Summary Component
export const CreditWorthinessSummaryCard: React.FC<{ data: CreditWorthinessSummary[] }> = ({ data }) => {
  // Debug logging
  console.log('CreditWorthinessSummaryCard data:', data, typeof data, Array.isArray(data));
  
  // Handle cases where data might be an empty object or invalid
  if (!data || !Array.isArray(data) || !data.length) return null;

  const summary = data[0]; // Latest summary

  // Validate that summary has required properties
  if (!summary || typeof summary !== 'object') return null;

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'low risk': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium risk': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'high risk': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <AnalysisCard title="Credit Worthiness Summary">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Score */}
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900 mb-2">
            {summary.TotalScore ? summary.TotalScore.toFixed(1) : '0.0'}
          </div>
          <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getRiskColor(summary.RiskLevel || 'Unknown')}`}>
            {summary.RiskLevel || 'Unknown'}
          </div>
          <p className="text-sm text-gray-600 mt-2">Overall Score</p>
        </div>

        {/* Key Metrics */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Monthly Balance:</span>
            <span className="font-semibold">{formatCurrency(summary.AverageMonthlyBalance || 0)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Income Consistency:</span>
            <span className={`font-semibold ${
              summary.IncomeConsistencyRating === 'Stable' ? 'text-green-600' : 'text-red-600'
            }`}>
              {summary.IncomeConsistencyRating || 'Unknown'}
            </span>
          </div>
          {/* <div className="flex justify-between items-center">
            <span className="text-gray-600">Savings Rate:</span>
            <span className="font-semibold text-blue-600">{summary.SavingsRate}%</span>
          </div> */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Discretionary Spending:</span>
            <span className="font-semibold">{summary.DiscretionaryProportion || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Spending Analysis */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <PieChart className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Spending Pattern</span>
        </div>
        <div className={`text-sm ${
          summary.DiscretionarySpendingFlag === 'Low Discretionary Spending' 
            ? 'text-green-600' 
            : 'text-orange-600'
        }`}>
          {summary.DiscretionarySpendingFlag || 'Unknown'}
        </div>
      </div>
    </AnalysisCard>
  );
};

// Investment Analysis Component
export const InvestmentAnalysis: React.FC<{ 
  transactions: InvestmentTransaction[]; 
  ratio: InvestmentRatio[] 
}> = ({ transactions, ratio }) => {
  // Handle cases where data might be empty objects or invalid
  if ((!transactions || !Array.isArray(transactions) || !transactions.length) && 
      (!ratio || !Array.isArray(ratio) || !ratio.length)) return null;

  const totalInvestmentAmount = transactions.reduce((sum, t) => sum + (t.Debit || 0), 0);
  const investmentRatio = ratio.length > 0 ? ratio[0].InvestmentRatio : 0;

  return (
    <AnalysisCard title="Investment Analysis">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <Target className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(totalInvestmentAmount)}</div>
          <div className="text-sm text-gray-600">Total Investments</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <BarChart3 className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{investmentRatio.toFixed(2)}%</div>
          <div className="text-sm text-gray-600">Investment Ratio</div>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <Activity className="w-6 h-6 text-purple-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{transactions.length}</div>
          <div className="text-sm text-gray-600">Transactions</div>
        </div>
      </div>

      {/* Investment Transactions */}
      {transactions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.slice(0, 5).map((transaction, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(transaction.value_date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {transaction.description}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 text-right">
                    {formatCurrency(transaction.Debit || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AnalysisCard>
  );
};

// Risk Indicators Component
export const RiskIndicators: React.FC<{ 
  exceptionalTransactions: ExceptionalTransaction[];
  monthlyOverdraft: MonthlyOverdraft[];
  highValueTransactions: HighValueTransaction[];
}> = ({ exceptionalTransactions, monthlyOverdraft, highValueTransactions }) => {
  
  const cashDeposits = exceptionalTransactions.filter(t => 
    t.Category.includes('Cash Deposit') && t.TransactionType === 'Credit'
  );
  const cashWithdrawals = exceptionalTransactions.filter(t => 
    t.Category.includes('Cash Withdrawal') && t.TransactionType === 'Debit'
  );
  
  const totalOverdrafts = monthlyOverdraft.reduce((sum, od) => sum + od.OverdraftCount, 0);
  const highValueCount = highValueTransactions.filter(hvt => hvt.IsHighValue === 'Yes').length;

  return (
    <AnalysisCard title="Risk Indicators">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{cashWithdrawals[0]?.Number || 0}</div>
          <div className="text-sm text-gray-600">Large Cash Withdrawals</div>
        </div>
        
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <CreditCard className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{totalOverdrafts}</div>
          <div className="text-sm text-gray-600">Overdraft Incidents</div>
        </div>
        
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <Zap className="w-6 h-6 text-orange-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{highValueCount}</div>
          <div className="text-sm text-gray-600">High Value Transactions</div>
        </div>
        
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <IndianRupee className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{cashDeposits[0]?.Number || 0}</div>
          <div className="text-sm text-gray-600">Large Cash Deposits</div>
        </div>
      </div>

      {/* Risk Details */}
      <div className="mt-6 space-y-3">
        {monthlyOverdraft.length > 0 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">Overdraft History</span>
            </div>
            <div className="text-sm text-yellow-700 mt-1">
              Recent overdraft activity detected in {monthlyOverdraft.length} month(s)
            </div>
          </div>
        )}
        
        {highValueCount > 0 && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Activity className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">High Value Activity</span>
            </div>
            <div className="text-sm text-orange-700 mt-1">
              {highValueCount} transactions flagged as high value relative to average income
            </div>
          </div>
        )}
      </div>
    </AnalysisCard>
  );
};

// Loan and Expense Ratios Component
export const LoanExpenseRatios: React.FC<{ 
  loanRatio: LoanPaymentRatio[];
  expenseRatio: ExpenseRatio[];
}> = ({ loanRatio, expenseRatio }) => {
  
  if (!loanRatio.length && !expenseRatio.length) return null;

  const loan = loanRatio[0];
  const expense = expenseRatio[0];

  return (
    <AnalysisCard title="Financial Ratios">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Loan Payment Ratio */}
        {loan && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-blue-900">Loan Payment Ratio</h4>
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-blue-900 mb-2">{loan.LoanRatio.toFixed(2)}%</div>
            <div className="space-y-1 text-xs text-blue-700">
              <div>Total Loans: {loan.TotalLoanRepayments}</div>
              <div>Total Debits: {loan.TotalDebits}</div>
            </div>
          </div>
        )}

        {/* Expense Ratio */}
        {expense && (
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-green-900">Non-Loan Expense Ratio</h4>
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-900 mb-2">{expense.ExpenseRatio.toFixed(2)}%</div>
            <div className="space-y-1 text-xs text-green-700">
              <div>Non-Loan Expenses: {expense.TotalNonLoanExpenses}</div>
              <div>Total Debits: {expense.TotalDebits}</div>
            </div>
          </div>
        )}
      </div>

      {/* Interpretation */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Analysis</h4>
        <div className="text-sm text-gray-700 space-y-1">
          {loan && (
            <div>
              • Loan payments represent {loan.LoanRatio.toFixed(1)}% of total expenses
              {loan.LoanRatio > 40 && <span className="text-red-600"> (High loan burden)</span>}
              {loan.LoanRatio < 20 && <span className="text-green-600"> (Manageable loan burden)</span>}
            </div>
          )}
          {expense && (
            <div>
              • Non-loan expenses are {expense.ExpenseRatio.toFixed(1)}% of total debits
              {expense.ExpenseRatio > 80 && <span className="text-orange-600"> (High discretionary spending)</span>}
            </div>
          )}
        </div>
      </div>
    </AnalysisCard>
  );
};

// Irregular Income Analysis Component
export const IrregularIncomeAnalysis: React.FC<{ data: IrregularIncome[] }> = ({ data }) => {
  if (!data.length) return null;

  const totalIrregularAmount = data.reduce((sum, item) => sum + item.credits, 0);
  const flaggedTransactions = data.filter(item => item.IrregularIncomeFlag === 'Yes');

  return (
    <AnalysisCard title="Irregular Income Analysis">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{flaggedTransactions.length}</div>
          <div className="text-sm text-gray-600">Irregular Transactions</div>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <IndianRupee className="w-6 h-6 text-orange-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(totalIrregularAmount)}</div>
          <div className="text-sm text-gray-600">Total Irregular Amount</div>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <Activity className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">
            {((flaggedTransactions.length / data.length) * 100).toFixed(1)}%
          </div>
          <div className="text-sm text-gray-600">Irregular Income Rate</div>
        </div>
      </div>

      {flaggedTransactions.length > 0 && (
        <div>
          <h4 className="text-md font-semibold mb-3 text-orange-600">Flagged Irregular Income Transactions</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flaggedTransactions.slice(0, 10).map((transaction, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(transaction.value_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={transaction.description}>
                        {transaction.description}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-orange-600 text-right">
                      {formatCurrency(transaction.credits)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                      {transaction.MonthYear}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {flaggedTransactions.length > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Risk Assessment</span>
          </div>
          <div className="text-sm text-yellow-700 mt-1">
            {flaggedTransactions.length > 5 
              ? "High risk: Multiple irregular income patterns detected. This may indicate unstable income sources."
              : flaggedTransactions.length > 2
              ? "Medium risk: Some irregular income patterns detected. Monitor for consistency."
              : "Low risk: Minimal irregular income patterns detected."
            }
          </div>
        </div>
      )}
    </AnalysisCard>
  );
};

// Sudden Large Withdrawal Analysis Component
export const SuddenLargeWithdrawalAnalysis: React.FC<{ data: SuddenLargeWithdrawal[] }> = ({ data }) => {
  if (!data.length) return null;

  const totalWithdrawalAmount = data.reduce((sum, item) => sum + item.debits, 0);
  const flaggedWithdrawals = data.filter(item => item.SuddenLargeWithdrawalFlag === 'Yes');
  const averageWithdrawal = totalWithdrawalAmount / data.length;

  return (
    <AnalysisCard title="Sudden Large Withdrawal Analysis">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{flaggedWithdrawals.length}</div>
          <div className="text-sm text-gray-600">Large Withdrawals</div>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <IndianRupee className="w-6 h-6 text-orange-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(totalWithdrawalAmount)}</div>
          <div className="text-sm text-gray-600">Total Amount</div>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <BarChart3 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(averageWithdrawal)}</div>
          <div className="text-sm text-gray-600">Average Amount</div>
        </div>
      </div>

      {flaggedWithdrawals.length > 0 && (
        <div>
          <h4 className="text-md font-semibold mb-3 text-red-600">Flagged Large Withdrawal Transactions</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flaggedWithdrawals.slice(0, 10).map((withdrawal, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(withdrawal.value_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={withdrawal.description}>
                        {withdrawal.description}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-red-600 text-right">
                      {formatCurrency(withdrawal.debits)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                      {withdrawal.MonthYear}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {flaggedWithdrawals.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-800">Risk Assessment</span>
          </div>
          <div className="text-sm text-red-700 mt-1">
            {flaggedWithdrawals.length > 3 
              ? "High risk: Multiple large withdrawals detected. This may indicate financial stress or irregular spending patterns."
              : flaggedWithdrawals.length > 1
              ? "Medium risk: Some large withdrawals detected. Monitor withdrawal patterns for unusual activity."
              : "Low risk: Minimal large withdrawal activity detected."
            }
          </div>
        </div>
      )}
    </AnalysisCard>
  );
};

// Enhanced High Value Transaction Analysis Component
export const HighValueTransactionAnalysis: React.FC<{ data: HighValueTransaction[] }> = ({ data }) => {
  if (!data.length) return null;

  const highValueTransactions = data.filter(item => item.IsHighValue === 'Yes');
  const totalHighValueAmount = highValueTransactions.reduce((sum, item) => sum + item.debits, 0);
  const averageIncome = data.length > 0 ? data[0].avrageincome : 0;

  return (
    <AnalysisCard title="High Value Transaction Analysis">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <Zap className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{highValueTransactions.length}</div>
          <div className="text-sm text-gray-600">High Value Transactions</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <IndianRupee className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(totalHighValueAmount)}</div>
          <div className="text-sm text-gray-600">Total Amount</div>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <Target className="w-6 h-6 text-purple-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">{formatCurrency(averageIncome)}</div>
          <div className="text-sm text-gray-600">Average Income</div>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <BarChart3 className="w-6 h-6 text-orange-600 mx-auto mb-2" />
          <div className="text-lg font-bold text-gray-900">
            {averageIncome > 0 ? ((totalHighValueAmount / averageIncome) * 100).toFixed(1) : '0'}%
          </div>
          <div className="text-sm text-gray-600">Ratio to Income</div>
        </div>
      </div>

      {highValueTransactions.length > 0 && (
        <div>
          <h4 className="text-md font-semibold mb-3 text-blue-600">High Value Transaction Details</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Income</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Risk Flag</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {highValueTransactions.map((transaction, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(transaction.value_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 text-right">
                      {formatCurrency(transaction.debits)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {formatCurrency(transaction.avrageincome)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.IsHighValue === 'Yes' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {transaction.IsHighValue === 'Yes' ? 'High Risk' : 'Normal'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {highValueTransactions.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Analysis Summary</span>
          </div>
          <div className="text-sm text-blue-700 mt-1">
            {highValueTransactions.length > 5 
              ? "Frequent high-value transactions detected. This may indicate significant financial activity or potential risk."
              : highValueTransactions.length > 2
              ? "Moderate high-value transaction activity. Regular monitoring recommended."
              : "Low high-value transaction activity. Transaction patterns appear normal."
            }
          </div>
        </div>
      )}
    </AnalysisCard>
  );
};